import React from 'react';
import styled from 'styled-components';
import { Border, Breakpoints, Color, Grid, Type } from '../../../app/StyleGuide';
import { SmallRadioRoundedIconButton } from '../../../components/Buttons';
import { RaisedContainer } from '../../../components/Layout';
import { ChurchImage, ListItemTitleContainer } from '../../../components/PairingElements';

type SelectOrgItem = {
  id: string;
  name: string;
  logo?: string;
  members?: unknown[];
};

type SelectOrgListProps = {
  orgs: SelectOrgItem[];
  selectedOrgId: string | undefined;
  onSelectOrg: (orgId: string) => void;
  onCreateOrg?: () => void;
};

const ChurchList = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: ${Grid._6};
  margin-top: ${Grid._10};

  @media screen and (min-width: ${Breakpoints.screen_sm}) {
    grid-template-columns: 1fr 1fr;
  }
`;

const ListItemContainer = styled.div`
  display: grid;
  grid-template-columns: auto 1fr auto;
  grid-gap: ${Grid._4};
  padding: ${Grid._4};
  width: 100%;

  > div:last-child {
    display: grid;
    place-content: center;
  }

  @media screen and (min-width: ${Breakpoints.screen_sm}) {
    padding-right: ${Grid._5};
  }
`;

const RaisedItemContainer = styled(RaisedContainer)`
  width: 100%;
  padding: 0;
`;

const CreateChurchContainer = styled(ListItemContainer)`
  border: 1px dashed ${Color.Gray._50};
  border-radius: ${Border.radius};
  grid-template-columns: 1fr auto;

  i.fa-plus {
    font-size: ${Type.Scale._5};
  }

  button {
    background: none;
    border: none;
    padding-right: 6px;
  }
`;

const ClickableRaisedContainer = styled(RaisedItemContainer)`
  cursor: pointer;
`;

const OrgListItem = ({ org, selected, onSelect }: { org: SelectOrgItem; selected: boolean; onSelect: () => void }) => (
  <ClickableRaisedContainer onClick={onSelect}>
    <ListItemContainer>
      <ChurchImage church={org} />
      <ListItemTitleContainer>
        <h4>{org.name}</h4>
        {org.members && (
          <p>
            {org.members.length} {org.members.length === 1 ? 'Person' : 'People'}
          </p>
        )}
      </ListItemTitleContainer>
      <div>
        <SmallRadioRoundedIconButton>
          {selected ? <i className="fas fa-circle color-complete" /> : <i />}
        </SmallRadioRoundedIconButton>
      </div>
    </ListItemContainer>
  </ClickableRaisedContainer>
);

const ClickableCreateContainer = styled(CreateChurchContainer)`
  cursor: pointer;
`;

const CreateOrgItem = ({ onClick }: { onClick: () => void }) => (
  <ClickableCreateContainer onClick={onClick}>
    <ListItemTitleContainer>
      <h4>Create Another Church</h4>
      <p>Don't see your church? Create a new one.</p>
    </ListItemTitleContainer>
    <div>
      <i className="fas fa-plus" />
    </div>
  </ClickableCreateContainer>
);

export const SelectOrgList = ({ orgs, selectedOrgId, onSelectOrg, onCreateOrg }: SelectOrgListProps) => (
  <ChurchList>
    {orgs.map(org => (
      <OrgListItem org={org} selected={org.id === selectedOrgId} onSelect={() => onSelectOrg(org.id)} key={org.id} />
    ))}
    {onCreateOrg && <CreateOrgItem onClick={onCreateOrg} />}
  </ChurchList>
);
